import { AfterViewInit, Directive, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { AbstractBraintreeComponent } from '@df/module-braintree/abstract-braintree.component';
import { Braintree } from '@df/module-braintree/braintree.namespace';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class AbstractBraintreeButtonComponent<
        T extends Braintree.IPaymentMethod,
        D extends Braintree.IApplePayInstance | Braintree.IGooglePayInstance
    >
    extends AbstractBraintreeComponent<T>
    implements OnInit, AfterViewInit
{
    /**
     * Payment method available status
     * @type {Status}
     */
    availableStatus = new Status();

    /**
     * Payment in progress status
     * @type {Status}
     */
    inProgressStatus = new Status();

    /**
     * Braintree button pay instance
     * @type {D}
     */
    protected braintreeButtonInstance: D | undefined;

    /**
     * Braintree client instance
     * @type {Braintree.IClientInstance}
     */
    protected braintreeClientInstance: Braintree.IClientInstance | undefined;

    /**
     * Payment button html element
     * @type {Element}
     */
    protected buttonElement: Element | undefined;

    /**
     * Component container
     * important: you need to put #container on div element in template
     */
    @ViewChild('container')
    protected container?: ElementRef<any>;

    /**
     * HTMLElement object for container where we mount payment button
     * @type {HTMLElement}
     */
    protected containerElement?: HTMLElement;

    /**
     * error during payment processing message
     * @type {string}
     */
    protected readonly paymentErrorMessage = 'There was an error processing your payment.';

    /**
     * We overwrite parent ngOnInit, we have to init component in ngAfterViewInit because of 'container' ViewChild
     */
    override ngOnInit() {}

    override ngAfterViewInit() {
        super.ngAfterViewInit();

        this.containerElement = this.getContainerElement();

        if (this.containerElement) {
            if (this.isAvailable()) {
                this.app.logger.info('Apple Pay is available');

                super.ngOnInit();
            } else {
                this.onAvailableError();
            }
        } else {
            this.onScriptLoadError();
        }
    }

    /**
     * Checks if we can use this component
     * @returns {boolean}
     */
    protected abstract isAvailable(): boolean;

    /**
     * on available error callback
     */
    protected onAvailableError(): void {}

    //////////////////////////////////////////
    /////
    /////   Script methods
    /////
    //////////////////////////////////////////

    protected override onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();
        this.app.logger.info('Braintree script loaded');

        if (this.canAddButton()) {
            const client = this.braintreeService.getClient();
            const token = this.getClientToken();
            this.app.logger.info('Braintree client token', token);

            if (client && token) {
                client
                    .create({ authorization: token })
                    .then(clientInstance => {
                        this.braintreeClientInstance = clientInstance;

                        this.getBraintreeButtonInstance(clientInstance).then(buttonInstance => {
                            this.braintreeButtonInstance = buttonInstance;

                            this.addButton();
                        });
                    })
                    .catch(() => this.onAddButtonError());
            } else {
                this.onAddButtonError();
            }
        }
    }

    /**
     * Returns braintree button payment instance
     * @param clientInstance
     * @returns {Promise<D>}
     */
    protected abstract getBraintreeButtonInstance(clientInstance: Braintree.IClientInstance): Promise<D>;

    /**
     * Checks if we can start adding button, every required scripts loaded etc
     * @returns {boolean}
     */
    protected abstract canAddButton(): boolean;

    /**
     * On add button error callback
     */
    protected abstract onAddButtonError(): void;

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns checkout status
     * @returns {Status}
     */
    get checkoutStatus(): Status {
        return this.checkoutFormComponent.status;
    }

    /**
     * On checkout submit error callback
     * @param {ICheckoutSubmitErrorEventData} data
     */
    protected onCheckoutError(data: Checkout.ISubmitErrorEventData): void {}

    /**
     * Check if all steps in checkout are valid.
     * Used to show additional last step or content if entire checkout is valid.
     * (e.g on OB show 'Review your order' section)
     * @returns {boolean}
     */
    protected get isCheckoutValid(): boolean {
        return this.checkoutFormComponent.isCheckoutValid;
    }

    /**
     * Validate checkout (e.g on submit) and scroll to first error if any.
     */
    protected validateCheckout(): void {
        this.checkoutFormComponent.validateCheckout();
    }

    //////////////////////////////////////////
    /////
    /////   Button
    /////
    //////////////////////////////////////////

    /**
     * Adds payment pay button to DOM
     */
    protected addButton(): void {
        const button = this.getButton();

        if (this.containerElement && button) {
            this.containerElement.appendChild(button);
            this.buttonElement = button;
        } else {
            this.onScriptLoadError();
        }
    }

    /**
     * Returns payment button
     * @returns {HTMLButtonElement}
     */
    protected abstract getButton(): HTMLButtonElement | undefined;

    /**
     * Returns html element fo button
     * @returns {HTMLElement | undefined}
     */
    protected getContainerElement(): HTMLElement | undefined {
        return this.container && this.container.nativeElement;
    }
}
